import JSZip from "jszip";
import { CellType, FletchyVault, ImportResult, Note, Notebook, SCHEMA_VERSION } from "./types";
import { createCell, createId, createImportResult, nowIso, normalizeVault } from "./vault";

interface QuiverContent {
  title?: string;
  cells?: Array<{
    type?: string;
    data?: unknown;
    language?: string;
  }>;
}

interface QuiverMeta {
  title?: string;
  name?: string;
}

interface QuiverImport {
  vault: FletchyVault;
  result: ImportResult;
}

function decodeHtml(value: string): string {
  return value
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, "\"")
    .replace(/&#39;/g, "'");
}

function htmlToPlainText(value: string): string {
  return decodeHtml(
    value
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]+>/g, "")
      .replace(/\n{3,}/g, "\n\n")
      .trim(),
  );
}

function stringFromUnknown(value: unknown): string {
  if (typeof value === "string") {
    return value;
  }

  if (value == null) {
    return "";
  }

  return JSON.stringify(value, null, 2);
}

function safeJsonParse<T>(value: string): T | undefined {
  try {
    return JSON.parse(value) as T;
  } catch {
    return undefined;
  }
}

function notebookPathFor(contentPath: string): string {
  const parts = contentPath.split("/");
  const index = parts.findIndex((part) => part.endsWith(".qvnotebook"));
  return index >= 0 ? parts.slice(0, index + 1).join("/") : "Imported Quiver";
}

function noteTitle(content: QuiverContent, noteMeta?: QuiverMeta): string {
  return noteMeta?.title || noteMeta?.name || content.title || "Imported Note";
}

function mapCellType(rawType: string | undefined, language: string | undefined): CellType | undefined {
  const type = rawType?.toLowerCase();
  const normalizedLanguage = language?.toLowerCase();

  if (type === "markdown") {
    return "markdown";
  }

  if (type === "code") {
    return normalizedLanguage === "mermaid" ? "mermaid" : "code";
  }

  if (type === "latex" || type === "math") {
    return "latex";
  }

  if (type === "text" || type === "richtext") {
    return "text";
  }

  if (type === "diagram" || type === "mermaid") {
    return "mermaid";
  }

  return undefined;
}

export async function importQuiverArchive(file: Blob): Promise<QuiverImport> {
  const zip = await JSZip.loadAsync(await file.arrayBuffer());
  const now = nowIso();
  const notebooksByPath = new Map<string, Notebook>();
  const notes: Note[] = [];
  const cells = [];
  const warnings: string[] = [];
  const skippedCounts: Record<string, number> = {};
  const importedCounts: Record<string, number> = {
    notebooks: 0,
    notes: 0,
    cells: 0,
  };

  const contentFiles = Object.values(zip.files)
    .filter((entry) => !entry.dir && entry.name.endsWith("/content.json"))
    .sort((a, b) => a.name.localeCompare(b.name));

  const attachmentCount = Object.values(zip.files).filter(
    (entry) => !entry.dir && entry.name.includes("/resources/"),
  ).length;

  if (attachmentCount > 0) {
    skippedCounts.attachments = attachmentCount;
    warnings.push(`${attachmentCount} attachment file(s) were skipped. Attachment import is planned after V1.`);
  }

  for (const contentFile of contentFiles) {
    const content = safeJsonParse<QuiverContent>(await contentFile.async("string"));
    if (!content) {
      skippedCounts.notes = (skippedCounts.notes ?? 0) + 1;
      warnings.push(`Skipped invalid Quiver note content: ${contentFile.name}`);
      continue;
    }

    const notebookPath = notebookPathFor(contentFile.name);
    let notebook = notebooksByPath.get(notebookPath);

    if (!notebook) {
      const metaEntry = zip.file(`${notebookPath}/meta.json`);
      const meta = metaEntry ? safeJsonParse<QuiverMeta>(await metaEntry.async("string")) : undefined;
      notebook = {
        id: createId("nb"),
        title: meta?.title || meta?.name || notebookPath.split("/").at(-1)?.replace(".qvnotebook", "") || "Imported Quiver",
        sortOrder: notebooksByPath.size,
        createdAt: now,
        updatedAt: now,
      };
      notebooksByPath.set(notebookPath, notebook);
      importedCounts.notebooks += 1;
    }

    const noteDirectory = contentFile.name.replace(/\/content\.json$/, "");
    const noteMetaEntry = zip.file(`${noteDirectory}/meta.json`);
    const noteMeta = noteMetaEntry ? safeJsonParse<QuiverMeta>(await noteMetaEntry.async("string")) : undefined;
    const note: Note = {
      id: createId("note"),
      notebookId: notebook.id,
      title: noteTitle(content, noteMeta),
      tags: [],
      favorite: false,
      createdAt: now,
      updatedAt: now,
      cellIds: [],
    };

    for (const [index, quiverCell] of (content.cells ?? []).entries()) {
      const type = mapCellType(quiverCell.type, quiverCell.language);
      if (!type) {
        skippedCounts.cells = (skippedCounts.cells ?? 0) + 1;
        warnings.push(`Skipped unsupported cell type "${quiverCell.type ?? "unknown"}" in ${note.title}.`);
        continue;
      }

      const rawContent = stringFromUnknown(quiverCell.data);
      const contentText = type === "text" ? htmlToPlainText(rawContent) : rawContent;
      const cell = createCell(note.id, type, contentText, index, {
        language: type === "code" ? quiverCell.language || "text" : undefined,
      });
      cells.push(cell);
      note.cellIds.push(cell.id);
      importedCounts.cells += 1;
    }

    if (note.cellIds.length === 0) {
      const fallbackCell = createCell(note.id, "text", "", 0);
      cells.push(fallbackCell);
      note.cellIds.push(fallbackCell.id);
      warnings.push(`Imported empty note "${note.title}" with a blank text cell.`);
    }

    notes.push(note);
    importedCounts.notes += 1;
  }

  if (notes.length === 0) {
    warnings.push("No Quiver notes were found in the selected archive.");
  }

  const vault = normalizeVault({
    schemaVersion: SCHEMA_VERSION,
    notebooks: Array.from(notebooksByPath.values()),
    notes,
    cells,
    settings: {
      theme: "system",
      splitPreview: true,
    },
    createdAt: now,
    updatedAt: now,
  });

  return {
    vault,
    result: createImportResult(importedCounts, skippedCounts, warnings),
  };
}
